import LgpdNotice from '@/components/feature/LgpdNotice';
import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const faqs = [
  {
    q: 'Qual o número mínimo e máximo de fones para uma festa silenciosa?',
    a: 'Atendemos eventos a partir de 20 convidados. O teto atual é de 1.500 fones simultâneos — ideal para festivais e grandes celebrações. Para eventos maiores, entre em contato para avaliar soluções customizadas.',
  },
  {
    q: 'Quantos DJs podem tocar ao mesmo tempo na silent disco?',
    a: 'Vários DJs simultâneos, cada um transmitindo em um canal independente. Os convidados escolhem qual canal ouvir direto no fone. Os LEDs coloridos (vermelho, verde e azul) identificam visualmente quem está em cada canal.',
  },
  {
    q: 'Os fones funcionam em ambientes ao ar livre?',
    a: 'Sim — e é um dos formatos mais procurados! O sinal RF alcança até 500 metros em campo aberto. Para áreas muito extensas, reforçamos com antenas direcionais. Parques, praças, jardins e praias são cenários ideais para Silent Disco.',
  },
  {
    q: 'Com que antecedência preciso contratar?',
    a: 'Para festas de até 200 convidados, recomendamos 15 dias de antecedência. Para eventos maiores, 30 dias. Em casos urgentes, entre em contato para verificar disponibilidade — fazemos o possível para atender.',
  },
  {
    q: 'O que está incluso além dos fones?',
    a: 'O pacote completo inclui: fones profissionais com LED, transmissores de áudio, microfones sem fio para DJs, montagem completa, suporte técnico presencial durante toda a festa, desmontagem, higienização e 10% de fones extras como reserva — sem custo adicional.',
  },
  {
    q: 'Os fones são higienizados entre os eventos?',
    a: 'Sim — protocolo rigoroso. Todos os fones passam por higienização com álcool isopropílico 70% antes e após cada evento. Para festas maiores, também oferecemos capas descartáveis individuais para máxima tranquilidade.',
  },
  {
    q: 'A STIB atende fora de São Paulo?',
    a: 'Sim. Atendemos todo o Brasil — São Paulo, Rio de Janeiro, Brasília, Belo Horizonte, Curitiba, Porto Alegre, Recife, Fortaleza, Salvador e demais cidades. Entre em contato para verificar disponibilidade e condições para a sua localidade.',
  },
  {
    q: 'Posso usar meus próprios DJs ou vocês fornecem?',
    a: 'Você pode usar seus próprios DJs ou nós podemos indicar parceiros de confiança. Nossa equipe técnica cuida de toda a parte de equipamentos e conecta os DJs ao sistema — seja qual for a sua escolha.',
  },
];

interface FormState {
  name: string;
  email: string;
  phone: string;
  eventType: string;
  participants: string;
  message: string;
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>({
    name: '', email: '', phone: '', eventType: '', participants: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.1 });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const formEl = e.currentTarget as HTMLFormElement;
      const formData = new FormData(formEl);

      // Honeypot check
      const honeypot = formData.get('website_alt') as string;
      if (honeypot && honeypot.trim() !== '') {
        setSubmitted(true);
        setForm({ name: '', email: '', phone: '', eventType: '', participants: '', message: '' });
        setSubmitting(false);
        return;
      }
      formData.delete('website_alt');

      const body = new URLSearchParams();
      formData.forEach((value, key) => body.append(key, value as string));

      const res = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = { code: 'Unknown' }; }

      if (res.ok && parsed?.code === 'OK') {
        setSubmitted(true);
        setForm({ name: '', email: '', phone: '', eventType: '', participants: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setError(serverMsg || 'Erro ao enviar. Tente novamente ou ligue para (11) 91777-6227.');
      }
    } catch {
      setError('Erro ao enviar. Tente novamente ou ligue para (11) 91777-6227.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* FAQ */}
      <section ref={faqRef} className="py-28 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
              FAQ
            </span>
            <h2
              className={`font-heading font-bold text-5xl text-secondary transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              perguntas frequentes
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-500 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer group"
                >
                  <h4 className="font-heading font-semibold text-base text-secondary group-hover:text-primary transition-colors pr-4">
                    <a href={`#faq-fs-${i}`}>{faq.q}</a>
                  </h4>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-45 bg-primary/10' : ''}`}>
                    <i className={`ri-add-line text-base ${openIndex === i ? 'text-primary' : 'text-gray-400'}`} />
                  </div>
                </button>
                {openIndex === i && (
                  <div className="px-6 pb-6">
                    <p className="font-body text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form CTA */}
      <section ref={formRef} className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text */}
            <div className={`transition-all duration-700 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
                Orçamento Gratuito
              </span>
              <h2 className="font-heading font-bold text-5xl text-secondary mb-6 leading-tight">
                leve silent disco<br />para sua festa
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-10">
                Preencha o formulário e nossa equipe retorna com uma proposta personalizada em até 4 horas úteis. Sem compromisso.
              </p>

              <div className="space-y-5">
                <a href="tel:+5511917776227" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary/10 group-hover:bg-primary transition-all duration-300 flex-shrink-0">
                    <i className="ri-phone-line text-primary group-hover:text-white text-2xl transition-colors duration-300" />
                  </div>
                  <div>
                    <div className="font-body text-xs text-gray-400 uppercase tracking-wider">Ligação ou WhatsApp</div>
                    <div className="font-heading font-bold text-secondary text-lg">(11) 91777-6227</div>
                  </div>
                </a>
                <a href="https://wa.me/5511917776227?text=Olá! Quero orçamento para Silent Disco / Festa Silenciosa." target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-green-50 group-hover:bg-green-500 transition-all duration-300 flex-shrink-0">
                    <i className="ri-whatsapp-line text-green-500 group-hover:text-white text-2xl transition-colors duration-300" />
                  </div>
                  <div>
                    <div className="font-body text-xs text-gray-400 uppercase tracking-wider">Resposta rápida</div>
                    <div className="font-heading font-bold text-secondary text-lg">Abrir WhatsApp</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div className={`bg-gray-50 rounded-3xl p-10 border border-gray-100 transition-all duration-700 delay-200 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-6">
                    <i className="ri-check-line text-green-500 text-4xl" />
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-secondary mb-3">Mensagem enviada!</h3>
                  <p className="font-body text-gray-600">Nossa equipe retornará em até 4 horas úteis com sua proposta personalizada de Silent Disco.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="fs-orcamento-form">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-body text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Nome *</label>
                        <input
                          type="text" name="name" value={form.name} onChange={handleChange} required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-primary focus:outline-none font-body text-sm bg-white"
                          placeholder="Seu nome"
                        />
                      </div>
                      <div>
                        <label className="block font-body text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Telefone *</label>
                        <input
                          type="tel" name="phone" value={form.phone} onChange={handleChange} required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-primary focus:outline-none font-body text-sm bg-white"
                          placeholder="(11) 9xxxx-xxxx"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-body text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">E-mail *</label>
                      <input
                        type="email" name="email" value={form.email} onChange={handleChange} required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-primary focus:outline-none font-body text-sm bg-white"
                        placeholder="seu@email.com"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-body text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Tipo de Evento *</label>
                        <select
                          name="eventType" value={form.eventType} onChange={handleChange} required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-primary focus:outline-none font-body text-sm bg-white"
                        >
                          <option value="">Selecione...</option>
                          <option value="Casamento">Casamento</option>
                          <option value="Confraternização Corporativa">Confraternização Corporativa</option>
                          <option value="Aniversário / Formatura">Aniversário / Formatura</option>
                          <option value="Festival / Evento ao Ar Livre">Festival / Evento ao Ar Livre</option>
                          <option value="Evento em Hotel / Resort">Evento em Hotel / Resort</option>
                          <option value="Festa Temática">Festa Temática</option>
                          <option value="Outro">Outro</option>
                        </select>
                      </div>
                      <div>
                        <label className="block font-body text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Convidados *</label>
                        <select
                          name="participants" value={form.participants} onChange={handleChange} required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-primary focus:outline-none font-body text-sm bg-white"
                        >
                          <option value="">Quantidade...</option>
                          <option value="20 a 50">20 a 50</option>
                          <option value="51 a 150">51 a 150</option>
                          <option value="151 a 300">151 a 300</option>
                          <option value="301 a 500">301 a 500</option>
                          <option value="Mais de 500">Mais de 500</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block font-body text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Informações Adicionais</label>
                      <textarea
                        name="message" value={form.message} onChange={handleChange}
                        maxLength={500} rows={3}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-primary focus:outline-none font-body text-sm bg-white resize-none"
                        placeholder="Data, cidade, número de DJs ou canais desejados..."
                      />
                      <p className="text-xs text-gray-400 mt-1">{form.message.length}/500</p>
                    </div>

                    {/* Honeypot — anti-spam */}
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="absolute opacity-0 pointer-events-none top-[-9999px] left-[-9999px]" />

                    {error && <p className="font-body text-sm text-red-500">{error}</p>}
                    <LgpdNotice kind="contato" className="mb-4 col-span-full" />
            <button
                      type="submit" disabled={submitting}
                      className="w-full bg-primary text-black font-body font-bold text-sm uppercase tracking-wide py-4 rounded-full hover:scale-[1.02] transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? 'Enviando...' : 'Solicitar Orçamento Gratuito'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}